import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import Banner from '../Components/Banner';
import { ChevronRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import LocationForm from '../Components/mumbai/LocationForm';
import ServiceNowLeadForm from '../Components/ServiceNowLeadForm';

function ServiceNowConsultingCompany() {
const servicenowFaqData = [
    {
        id: 1,
        question: "What are ServiceNow consulting services?",
        answer:
            "ServiceNow consulting helps businesses plan, optimize, customize, and improve their ServiceNow platform based on business and technical requirements."
    },
    {
        id: 2,
        question: "What does a ServiceNow consulting company do?",
        answer:
            "A ServiceNow consulting company helps evaluate your platform, improve workflows, optimize configurations, plan integrations, and align ServiceNow with business needs."
    },
    {
        id: 3,
        question: "Why do I need a ServiceNow consultant?",
        answer:
            "A ServiceNow consultant helps with platform configuration, workflows, integrations, automation, optimization, and other technical decisions."
    },
    {
        id: 4,
        question: "Which ServiceNow modules can you provide consulting for?",
        answer:
            "Mobikode provides consulting support for ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow automation, and other ServiceNow requirements."
    },
    {
        id: 5,
        question: "Can you assess and optimize our existing ServiceNow platform?",
        answer:
            "Yes. We can assess your ServiceNow environment, identify gaps, optimize workflows and configurations, and recommend improvements based on your business processes."
    },
    {
        id: 6,
        question: "Why choose Mobikode for ServiceNow consulting?",
        answer:
            "Mobikode provides ServiceNow consulting across strategy, platform assessment, optimization, workflows, integrations, automation, and technical requirements."
    }
];
    
          const [activeIndex, setActiveIndex] = useState(0);
          const [faqActiveIndex, setFaqActiveIndex] = useState(0);
    
    const toggleFaqAccordion = (index) => {
        setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
    };
  return (
    
    <div>
<Helmet>
    {/* Primary SEO */}
    <title>
        ServiceNow Consulting Company | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode is a ServiceNow consulting company providing expert consulting services for ServiceNow strategy, platform optimization, workflow automation, integrations, customization, and digital transformation."
    />

    <meta
        name="keywords"
        content="ServiceNow consulting company, ServiceNow consulting services, ServiceNow consulting firm, ServiceNow consultants, ServiceNow consulting partner, ServiceNow consultant, ServiceNow platform consulting, ServiceNow ITSM consulting, ServiceNow ITOM consulting, ServiceNow CSM consulting, ServiceNow HRSD consulting, ServiceNow integration consulting, ServiceNow strategy consulting"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/servicenow-consulting-company/"
    />


    {/* Open Graph */}
    <meta
        property="og:title"
        content="ServiceNow Consulting Company | Mobikode"
    />

    <meta
        property="og:description"
        content="Get expert ServiceNow consulting services for strategy, platform optimization, workflow automation, integrations, customization, and scalable ServiceNow solutions."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/servicenow-consulting-company/"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Mobikode"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/services/servicenow-consulting-company.jpg"
    />

    <meta
        property="og:image:alt"
        content="ServiceNow Consulting Company by Mobikode"
    />


    {/* Twitter / X */}
    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="ServiceNow Consulting Company | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Mobikode provides ServiceNow consulting services for strategy, platform optimization, ITSM, ITOM, CSM, HRSD, integrations, workflow automation, and ServiceNow transformation."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/services/servicenow-consulting-company.jpg"
    />

    <meta
        name="twitter:image:alt"
        content="ServiceNow Consulting Company by Mobikode"
    />
</Helmet>


<section className="relative h-[500px] lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
    <div className="absolute inset-0 z-0">
        <img
            src="/assets/services/bg3.png"
            alt="ServiceNow consulting company providing expert consulting services"
            className="h-full w-full object-cover opacity-40"
        />

        <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
    </div>

    <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

        {/* Hero Heading */}
        <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
        >
            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">
                ServiceNow Consulting Company
            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">
                Expert ServiceNow Consulting Services for Your Business
            </p>
        </motion.div>

        {/* Hero Description */}
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
        >
            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">
                Get expert ServiceNow consulting services to align your platform
                with business goals, operational requirements, and technical
                needs. Mobikode helps businesses optimize ServiceNow through
                strategic consulting, platform improvements, workflow
                automation, integrations, customization, and scalable solutions.
            </p>
        </motion.div>

    </div>
</section>

<section className="bg-[#FFFFFF] overflow-hidden lg:py-[100px] py-[60px]">
    <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

        <div className="flex flex-col lg:flex-row items-center gap-[40px] lg:gap-[80px]">

            {/* LEFT IMAGE */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-1/2"
            >
                <div className="w-full h-[280px] md:h-[380px] lg:h-[450px] overflow-hidden">
                    <img
                        src="/assets/sales/img2.png"
                        alt="ServiceNow consulting services and platform optimization"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                </div>
            </motion.div>

            {/* RIGHT CONTENT */}
            <div className="w-full lg:w-1/2 text-left">

                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-[10px] pb-[15px] lg:pb-[20px]"
                >
                    <motion.span className="font-[500] font-graphik text-[16px] lg:text-[20px] leading-[1.6] text-[#01386A]">
                        Service Overview
                    </motion.span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="font-[600] font-sora lg:text-[30px] max-w-[850px] w-full text-[20px] leading-[1.3] text-[#01386A]"
                >
                    Expert ServiceNow Consulting Services for Your Business
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35 }}
                    className="font-[400] max-w-[800px] w-full font-graphik text-[14px] lg:text-[16px] leading-[1.6] lg:leading-[22px] text-[#01386A] mt-[20px] lg:mt-[25px]"
                >
                    Mobikode provides ServiceNow consulting services to help
                    businesses align their ServiceNow platform with business
                    goals, operational processes, and technical requirements.
                    Our consulting expertise covers ServiceNow strategy,
                    platform optimization, workflow automation, integrations,
                    customization, and ongoing platform improvements.

                    <br /><br />

                    From evaluating your existing ServiceNow environment to
                    planning new platform capabilities, our consultants can
                    help identify opportunities for improvement and define
                    practical solutions around your specific requirements.
                    We work with your teams to support ServiceNow initiatives
                    across ITSM, ITOM, CMDB, CSM, HRSD, integrations, and
                    other platform capabilities.
                </motion.p>

                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "70px" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="h-[3px] bg-[#ED6800] mt-[25px] lg:mt-[35px]"
                />

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

            {/* LEFT SIDE */}
            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                    ServiceNow Consulting Expertise
                </h2>
            </div>


            {/* RIGHT SIDE */}
            <div className="w-full lg:w-[60%] flex flex-col gap-6">

                {/* Card 1 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Strategy & Consulting
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Define a practical ServiceNow strategy aligned with your
                        business objectives, operational processes, technical
                        requirements, and long-term platform roadmap.
                    </p>
                </div>


                {/* Card 2 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Platform Assessment
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Assess your existing ServiceNow environment to identify
                        configuration gaps, optimization opportunities, workflow
                        improvements, integration requirements, and areas for
                        platform enhancement.
                    </p>
                </div>


                {/* Card 3 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow ITSM Consulting
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Get ServiceNow ITSM consulting support for incident,
                        problem, change, request, and knowledge management,
                        including process improvements, workflow optimization,
                        and platform configuration.
                    </p>
                </div>


                {/* Card 4 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow ITOM & CMDB Consulting
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Improve IT operations and infrastructure visibility with
                        consulting across ServiceNow ITOM, CMDB, discovery,
                        service mapping, configuration management, and event
                        management.
                    </p>
                </div>


                {/* Card 5 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Integration Consulting
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Plan and optimize ServiceNow integrations with enterprise
                        applications using REST and SOAP APIs, IntegrationHub,
                        data synchronization, third-party systems, and automated
                        workflows.
                    </p>
                </div>


                {/* Card 6 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Optimization & Automation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Optimize ServiceNow workflows and automate business
                        processes through platform configuration, workflow
                        improvements, scripting, integrations, and process
                        automation aligned with your business requirements.
                    </p>
                </div>

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-white py-16 lg:py-24">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

        <h2 className="text-[#01386A] text-[20px] md:text-[30px] font-[600] font-sora mb-6 lg:mb-10 text-left">
            ServiceNow Consulting Expertise
        </h2>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">

            {/* LEFT SIDE - TABS */}
            <div className="lg:w-1/2 flex flex-col">

                {/* STRATEGY & CONSULTING */}
                <div
                    onClick={() => setActiveIndex(0)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 0
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 0
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Strategy & Consulting
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 0
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* PLATFORM ASSESSMENT */}
                <div
                    onClick={() => setActiveIndex(1)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 1
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 1
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Platform Assessment
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 1
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* ITSM CONSULTING */}
                <div
                    onClick={() => setActiveIndex(2)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 2
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 2
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITSM Consulting
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 2
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* ITOM & CMDB CONSULTING */}
                <div
                    onClick={() => setActiveIndex(3)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 3
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 3
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITOM & CMDB Consulting
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 3
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* INTEGRATION CONSULTING */}
                <div
                    onClick={() => setActiveIndex(4)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 4
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 4
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Integration Consulting
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 4
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* OPTIMIZATION & AUTOMATION */}
                <div
                    onClick={() => setActiveIndex(5)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 5
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 5
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Optimization & Automation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 5
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

            </div>


            {/* RIGHT SIDE - CONTENT */}
            <div className="lg:w-1/2 flex flex-col justify-start">

                <div className="min-h-[100px] mb-8">

                    {/* STRATEGY & CONSULTING */}
                    {activeIndex === 0 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Get strategic ServiceNow consulting aligned with your
                            business objectives, operational processes, technical
                            requirements, and long-term platform roadmap. Our
                            consultants help define practical ServiceNow
                            strategies and identify the right platform
                            capabilities for your organization.
                        </p>
                    )}


                    {/* PLATFORM ASSESSMENT */}
                    {activeIndex === 1 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Evaluate your existing ServiceNow environment with
                            expert platform consulting. Our team can identify
                            configuration gaps, process inefficiencies,
                            optimization opportunities, integration requirements,
                            and areas for improving overall platform performance.
                        </p>
                    )}


                    {/* ITSM CONSULTING */}
                    {activeIndex === 2 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Improve your ServiceNow ITSM capabilities with
                            consulting across incident, problem, change, request,
                            and knowledge management. Our consultants can help
                            optimize service management processes, workflows,
                            automation, and platform configuration.
                        </p>
                    )}


                    {/* ITOM & CMDB CONSULTING */}
                    {activeIndex === 3 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Strengthen IT operations and infrastructure visibility
                            with ServiceNow ITOM and CMDB consulting. Our team can
                            provide guidance across discovery, service mapping,
                            configuration management, event management, and
                            operational workflows.
                        </p>
                    )}


                    {/* INTEGRATION CONSULTING */}
                    {activeIndex === 4 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Plan and optimize ServiceNow integrations with
                            enterprise applications using REST and SOAP APIs,
                            IntegrationHub, data synchronization, third-party
                            systems, and automated workflows. Our consultants can
                            help define integration approaches around your
                            technical requirements.
                        </p>
                    )}


                    {/* OPTIMIZATION & AUTOMATION */}
                    {activeIndex === 5 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Optimize ServiceNow workflows and automate business
                            processes through platform configuration, workflow
                            improvements, scripting, integrations, and process
                            automation. Our consulting approach focuses on
                            improving platform efficiency while supporting
                            changing business requirements.
                        </p>
                    )}

                </div>


                {/* IMAGE */}
                <div className="w-full h-[300px] lg:h-[378px] overflow-hidden relative">

                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 to-transparent z-10 pointer-events-none"></div>


                    {/* STRATEGY */}
                    {activeIndex === 0 && (
                        <img
                            src="/assets/home-/ai-2.png"
                            alt="ServiceNow strategy and consulting services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* PLATFORM ASSESSMENT */}
                    {activeIndex === 1 && (
                        <img
                            src="/assets/home-/ai-3.png"
                            alt="ServiceNow platform assessment consulting"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* ITSM */}
                    {activeIndex === 2 && (
                        <img
                            src="/assets/home-/ai-4.png"
                            alt="ServiceNow ITSM consulting services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* ITOM & CMDB */}
                    {activeIndex === 3 && (
                        <img
                            src="/assets/home-/img1.png"
                            alt="ServiceNow ITOM and CMDB consulting services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* INTEGRATIONS */}
                    {activeIndex === 4 && (
                        <img
                            src="/assets/home-/img2.png"
                            alt="ServiceNow integration consulting services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* OPTIMIZATION */}
                    {activeIndex === 5 && (
                        <img
                            src="/assets/home-/img3.png"
                            alt="ServiceNow optimization and automation consulting"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                </div>

            </div>

        </div>
    </div>


    <style>{`
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(10px);
            }

            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
        }
    `}</style>

</section>




<section className="w-full pb-[40px] md:pb-[100px] bg-white">
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

        <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

            {/* LEFT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
                className="w-full lg:w-5/12 lg:sticky lg:top-[120px]"
            >
                <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2] lg:max-w-[350px]">
                    Frequently Asked Questions About ServiceNow Staff Augmentation
                </h2>
            </motion.div>


            {/* RIGHT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                    once: true,
                    amount: 0.1
                }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full lg:w-7/12 flex flex-col border border-[#E4E8F5] bg-white rounded-sm"
            >

                {servicenowFaqData.map((faq, index) => {

                    const isActive = faqActiveIndex === index;

                    return (
                        <div
                            key={faq.id}
                            className={`flex flex-col border-b ${
                                isActive
                                    ? "border-[#ED6800] border-b-[3px] md:border-b-[4px]"
                                    : "border-[#E4E8F5]"
                            } last:border-b-0 transition-colors duration-300`}
                        >

                            {/* QUESTION */}
                            <button
                                type="button"
                                onClick={() => toggleFaqAccordion(index)}
                                className="w-full flex justify-between items-center text-left py-[18px] sm:py-[24px] md:py-[32px] px-[16px] sm:px-[24px] md:px-[40px] group cursor-pointer bg-white md:hover:bg-[#F8FAFF] transition-colors duration-300"
                                aria-expanded={isActive}
                            >

                                <h3
                                    className={`font-sora max-w-[85%] text-[16px] md:text-[20px] font-[600] pr-[16px] sm:pr-[20px] transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#01386A]"
                                            : "text-[#01386A] md:group-hover:text-[#ED6800]"
                                    }`}
                                >
                                    {faq.question}
                                </h3>


                                {/* PLUS / MINUS ICON */}
                                <div className="flex-shrink-0 text-[#01386A] transition-colors duration-300 md:group-hover:text-[#ED6800]">

                                    {isActive ? (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    ) : (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="12"
                                                y1="5"
                                                x2="12"
                                                y2="19"
                                            />

                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    )}

                                </div>

                            </button>


                            {/* ANSWER */}
                            <AnimatePresence initial={false}>

                                {isActive && (

                                    <motion.div
                                        initial={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        animate={{
                                            height: "auto",
                                            opacity: 1
                                        }}
                                        exit={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        transition={{
                                            duration: 0.4,
                                            ease: "easeInOut"
                                        }}
                                        className="overflow-hidden bg-white"
                                    >

                                        <div className="pb-[20px] sm:pb-[32px] px-[16px] sm:px-[24px] md:px-[40px]">

                                            <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6]">
                                                {faq.answer}
                                            </p>

                                        </div>

                                    </motion.div>

                                )}

                            </AnimatePresence>

                        </div>
                    );
                })}

            </motion.div>

        </div>

    </div>
</section>

 <ServiceNowLeadForm/>
<Banner
    title="ServiceNow Consulting Company"
    text="Get expert ServiceNow consulting services for strategy, platform optimization, workflow automation, integrations, and scalable ServiceNow solutions aligned with your business and technical requirements."
/>
    </div>
  )
}


export default ServiceNowConsultingCompany
